import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { currencyLabel, formatReportCurrency } from '../lib/reportCurrency';
import { PERIOD_LABELS, createReportSnapshot, exportReportExcel, exportReportPDF } from '../lib/reportExports';

const API = API_BASE_URL;
const PERIODS = ['daily', 'weekly', 'monthly'];

function reportError(error) {
  if (error.response?.status === 409) return 'Restaurant reporting currency is missing or unsupported. Contact an administrator to review its configuration.';
  if ([401, 403, 404].includes(error.response?.status)) return 'This report is not available to your account. Reload to refresh your authorized restaurants.';
  return 'Unable to load a valid report. Please retry or contact an administrator.';
}

const Reports = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [context, setContext] = useState({ owner: null, restaurants: [], loading: true, error: '' });
  const [restaurantId, setRestaurantId] = useState('');
  const [period, setPeriod] = useState('daily');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);

  // A changed account cannot retain a previous account's restaurant list or report.
  const contextCurrent = context.owner === user;
  const restaurants = contextCurrent ? context.restaurants : [];
  const selected = restaurants.some(restaurant => restaurant.id === restaurantId);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    generation.current += 1;
    setRestaurantId('');
    setReport(null);
    setError('');
    setContext({ owner: user, restaurants: [], loading: true, error: '' });
    if (user) {
      axios.get(`${API}/restaurants`, { withCredentials: true, signal: controller.signal }).then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data)) throw new Error('Invalid restaurant list');
        const authorized = data.filter(restaurant => typeof restaurant.id === 'string'
          && /^[a-f\d]{24}$/i.test(restaurant.id) && typeof restaurant.name === 'string');
        setContext({ owner: user, restaurants: authorized, loading: false, error: '' });
        setRestaurantId(authorized.length === 1 ? authorized[0].id : '');
      }).catch(() => {
        if (active) setContext({ owner: user, restaurants: [], loading: false, error: 'Unable to load your authorized restaurants. Reload the page to try again.' });
      });
    } else {
      setContext({ owner: user, restaurants: [], loading: false, error: 'Sign in to view reports.' });
    }
    return () => { active = false; controller.abort(); generation.current += 1; };
  }, [user]);

  useEffect(() => {
    const requestId = ++generation.current;
    const controller = new AbortController();
    setReport(null);
    setError('');
    setLoading(selected);
    if (selected) {
      axios.get(`${API}/analytics/reports`, {
        params: { restaurant_id: restaurantId, period },
        withCredentials: true, signal: controller.signal,
      }).then(({ data }) => {
        if (requestId !== generation.current) return;
        setReport(createReportSnapshot(data, restaurantId, period));
      }).catch(failure => {
        if (requestId === generation.current) { setReport(null); setError(reportError(failure)); }
      }).finally(() => {
        if (requestId === generation.current) setLoading(false);
      });
    }
    return () => { controller.abort(); generation.current += 1; };
  }, [user, selected, restaurantId, period, retry]);

  const invalidate = () => {
    generation.current += 1; // Also rejects responses arriving before the next effect runs.
    setReport(null);
    setError('');
    setLoading(true);
  };
  const chooseRestaurant = id => {
    if (id === restaurantId || (id && !restaurants.some(restaurant => restaurant.id === id))) return;
    invalidate();
    setRestaurantId(id);
  };
  const choosePeriod = value => {
    if (!PERIODS.includes(value) || value === period) return;
    invalidate();
    setPeriod(value);
  };
  const currentReport = contextCurrent && selected && !loading && !error
    && report?.restaurant.id === restaurantId && report?.period === period ? report : null;

  const exportCurrent = type => {
    if (!currentReport) return;
    try {
      if (type === 'excel') exportReportExcel(currentReport);
      else exportReportPDF(currentReport);
      toast.success(type === 'excel' ? 'Excel exported' : 'PDF exported');
    } catch {
      toast.error('Export failed. Please retry.');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>{t('reports')}</h1>
        <div className="flex gap-2">
          <Button onClick={() => exportCurrent('excel')} disabled={!currentReport} variant="outline" data-testid="export-excel-btn">
            <FileDown className="h-4 w-4 me-2" /> Excel
          </Button>
          <Button onClick={() => exportCurrent('pdf')} disabled={!currentReport} style={{ backgroundColor: '#E57200' }} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 me-2" /> PDF
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="report-restaurant" className="block text-sm font-medium">Restaurant</label>
        <select id="report-restaurant" className="w-full max-w-md rounded-md border bg-background px-3 py-2"
          value={contextCurrent ? restaurantId : ''} onChange={event => chooseRestaurant(event.target.value)}
          disabled={!contextCurrent || context.loading || restaurants.length === 0}>
          <option value="">Select a restaurant</option>
          {restaurants.map(restaurant => <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>)}
        </select>
        {(!contextCurrent || context.loading) && <p role="status">Loading authorized restaurants...</p>}
        {contextCurrent && context.error && <p role="alert">{context.error}</p>}
        {contextCurrent && !context.loading && !context.error && restaurants.length === 0 && <p role="status">No authorized restaurants are available.</p>}
        {restaurants.length > 1 && !selected && <p role="status">Select a restaurant to view its financial report.</p>}
        {!currentReport && <p className="text-sm text-muted-foreground">Exports are available after the selected report loads successfully.</p>}
      </div>

      {currentReport && (
        <div className="text-sm space-y-1" data-testid="report-context">
          <p>Restaurant: {currentReport.restaurant.name} · Period: {PERIOD_LABELS[currentReport.period]}</p>
          <p>Currency: {currentReport.currency} · Timezone: {currentReport.timezone}{currentReport.timezone_source === 'default' ? ' (default)' : ''}</p>
          <p>Start: {currentReport.start_local} · End (exclusive): {currentReport.end_local_exclusive}</p>
          <p>Generated at: {currentReport.generated_at}</p>
        </div>
      )}
      {loading && selected && <p role="status">Loading report...</p>}
      {error && selected && <div role="alert"><p>{error}</p><Button variant="outline" onClick={() => { invalidate(); setRetry(value => value + 1); }}>Retry report</Button></div>}

      <Tabs value={period} onValueChange={choosePeriod}>
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily">{t('dailySales')}</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">{t('weeklySales')}</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">{t('monthlySales')}</TabsTrigger>
        </TabsList>
        {PERIODS.map(value => (
          <TabsContent key={value} value={value}>
            <p className="my-3 text-sm text-muted-foreground">{PERIOD_LABELS[value]} — calendar-to-date</p>
            {currentReport && <SalesChart report={currentReport} />}
          </TabsContent>
        ))}
      </Tabs>

      {currentReport && (
        <Card className="border-border/40">
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('topSellingItems')}</CardTitle></CardHeader>
          <CardContent>
            {currentReport.top_items.length === 0 ? <p>No item sales in this reporting period.</p> : <>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={currentReport.top_items}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" />
                  <YAxis yAxisId="quantity" allowDecimals={false} stroke="#64748B" />
                  <YAxis yAxisId="money" orientation="right" stroke="#64748B" tickFormatter={value => formatReportCurrency(value, currentReport.currency)} />
                  <Tooltip formatter={(value, name, item) => [item.dataKey === 'gross_item_sales' ? formatReportCurrency(value, currentReport.currency) : value, name]} />
                  <Legend />
                  <Bar yAxisId="quantity" dataKey="units_sold" fill="#232D4B" name="Quantity Sold" />
                  <Bar yAxisId="money" dataKey="gross_item_sales" fill="#E57200" name={`Gross item sales (${currencyLabel(currentReport.currency)})`} />
                </BarChart>
              </ResponsiveContainer>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <caption className="sr-only">Top selling items for the selected report</caption>
                  <thead><tr><th scope="col">Item</th><th scope="col">Quantity Sold</th><th scope="col">Gross Item Sales</th></tr></thead>
                  <tbody>{currentReport.top_items.map((item, index) => <tr key={index}>
                    <td className="py-2">{item.name}</td><td>{item.units_sold}</td><td>{formatReportCurrency(item.gross_item_sales, currentReport.currency)}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            </>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SalesChart = ({ report }) => (
  <Card className="border-border/40">
    <CardHeader><CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Net receipts</CardTitle></CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={report.sales}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" stroke="#64748B" />
          <YAxis yAxisId="money" stroke="#64748B" tickFormatter={value => formatReportCurrency(value, report.currency)} />
          <YAxis yAxisId="orders" orientation="right" allowDecimals={false} stroke="#64748B" />
          <Tooltip formatter={(value, name, item) => [item.dataKey === 'net_receipts' ? formatReportCurrency(value, report.currency) : value, name]} />
          <Legend />
          <Line yAxisId="money" type="monotone" dataKey="net_receipts" stroke="#E57200" strokeWidth={2} name={`Net receipts (${currencyLabel(report.currency)})`} />
          <Line yAxisId="orders" type="monotone" dataKey="order_count" stroke="#232D4B" strokeWidth={2} name="Order Count" />
        </LineChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

export default Reports;
